import LArtwork from "@components/LArtwork";
import LBox from "@components/LBox";
import LPressable from "@components/LPressable";
import LText from "@components/LText";
import { fonts } from "@theme";
import dayjs from "dayjs";
import { CONCERT_DATE_COLOR, SectionText, SectionTitle } from "../shared";
import type {
  ConcertRef,
  InboxSection,
  OnNavigate,
  UpcomingConcertsPayload,
} from "../types";

const ConcertRow = ({
  concert,
  ctaText,
  onNavigate,
}: {
  concert: ConcertRef;
  ctaText: string;
  onNavigate: OnNavigate;
}) => (
  <LPressable
    onPress={() => onNavigate(`/access/${concert.id}`)}
    sentryEventName="inbox_concert_press"
  >
    <LBox alignItems="center" flexDirection="row" gap="s3">
      <LArtwork
        borderRadius="m"
        size={56}
        url={concert.artistImage ?? concert.coverImageUrl}
      />

      <LBox flex={1} gap="s0_5">
        {concert.startDateTime ? (
          <LText
            fontFamily={fonts.semiBold}
            fontSize={11}
            letterSpacing={0.5}
            lineHeight={14}
            style={{ color: CONCERT_DATE_COLOR }}
            textTransform="uppercase"
          >
            {dayjs(concert.startDateTime).format("ddd, MMM D, YYYY")}
          </LText>
        ) : null}
        <LText
          color="white"
          fontFamily={fonts.semiBold}
          fontSize={14}
          lineHeight={18}
          numberOfLines={1}
        >
          {concert.artistName}
          {concert.city ? ` · ${concert.city}` : ""}
        </LText>
        {concert.venue ? (
          <LText
            color="subGrey"
            fontFamily={fonts.regular}
            fontSize={13}
            lineHeight={17}
            numberOfLines={1}
          >
            {concert.venue}
          </LText>
        ) : null}
      </LBox>

      <LBox
        backgroundColor="whiteTranslucent10"
        borderColor="greyTranslucent30"
        borderRadius="max"
        borderWidth={1}
        px="s3"
        py="s1_5"
      >
        <LText
          color="white"
          fontFamily={fonts.semiBold}
          fontSize={12}
          lineHeight={15}
        >
          {ctaText}
        </LText>
      </LBox>
    </LBox>
  </LPressable>
);

/**
 * "Upcoming concerts" — copy followed by tappable concert rows, each routing to
 * its `/access/:id`. Mirrors the upcoming_concerts block (no section-level CTA).
 */
export const UpcomingConcertsSection = ({
  section,
  onNavigate,
}: {
  section: InboxSection;
  onNavigate: OnNavigate;
}) => {
  const { concerts } = section.payload as UpcomingConcertsPayload;

  return (
    <LBox gap="s3_5">
      <SectionTitle>{section.title}</SectionTitle>
      <SectionText>{section.text}</SectionText>

      <LBox gap="s3">
        {concerts.map((concert) => (
          <ConcertRow
            concert={concert}
            ctaText={section.ctaText}
            key={concert.id}
            onNavigate={onNavigate}
          />
        ))}
      </LBox>
    </LBox>
  );
};
